import React, { DragEvent, RefObject, useState } from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { PropertyType } from '@producer/properties';

import { CoverImage } from '../../../components/cover-image/cover-image';
import { SelectDrawer } from './select-drawer';
import { InputDrawer } from './input-drawer';
import { TextAreaDrawer } from '../../../components/drawer-inputs/text-area-drawer';
import { NumberInputDrawer } from '../../../components/drawer-inputs/number-input-drawer';
import { LocationSelect } from './location-select';
import { StoryboardItemHeader } from './StoryboardItemHeader';
import { Bloc } from '../../../routes/project/components/step-drawer';
import { CharactersSelect } from './characters-select';
import { formatPicture } from '../../../core/services/image.service';
import { getModifier } from '../../../core/services/event.service';
import { Shot, Storyboard, UpdateShotsInput } from '../../../app/entities';
import { TimeInputDrawer } from '../../../components/drawer-inputs/time-input-drawer';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { useSelection, type Modifier } from '../../../core/contexts/selection.context';
import { bulkUpdateStore } from '../../../core/stores/bulk-update';
import { TagsViewer } from '../../tags/components/tags-viewer';
import { openModal } from '../../../core/modal/open-modal';
import { compareArrays } from '../../../lib/utils/compare-two-arrays';

import { Property } from '../../../app/entities/property';
import { MultiSelectItem } from './multi-select.item';

export interface ShotCardProps {
  shotId: string;
  projectId: string;
  storyboardId: string;
  active?: boolean;
  position?: number;
  style?: any;
  onClick?: (shotId: string, modifier?: Modifier) => void;

  storyboardPreferences?: {
    zoom: number;
  };

  properties: Property[];

  ref: RefObject<HTMLDivElement>;
}

export const ShotCard: React.FC<ShotCardProps> = observer(
  ({
    shotId,
    projectId,
    storyboardId,
    position,
    active,
    style,
    onClick,
    storyboardPreferences,
    properties,
    ref,
    ...props
  }) => {
    const { elements: selectedShotsIds } = useSelection();

    const storyboard = Storyboard.getOne(storyboardId)!;
    const shot = Shot.getOne(shotId);

    if (!shot) {
      return null;
    }

    const isBulkUpdate = selectedShotsIds && selectedShotsIds.length > 1;

    const [expand, setExpand] = useState(false);

    const handleSingleEdit = (data: UpdateShotsInput) => {
      if (data.tagsIds) {
        shot?.updateTags(data.tagsIds);
        return;
      }

      shot && shot.update(data!);
    };

    const handleEdit = (data: any) => {
      if (!isBulkUpdate) {
        return handleSingleEdit(data);
      }

      const { tagsIds, ...rest } = data;

      bulkUpdateStore.handleShotsBulkUpdate(rest, selectedShotsIds, shot!);
    };

    const handleEditProperty = (propertyId: string, value: any) => {
      if (!isBulkUpdate) {
        return handleSingleEdit({
          properties: {
            ...shot.properties,
            [propertyId]: value,
          },
        });
      }

      bulkUpdateStore.handleShotsBulkUpdate(
        { properties: { [propertyId]: value } },
        selectedShotsIds,
        shot!,
      );
    };

    const handleCreateShot = async (file?: File) => {
      if (!shot?.project?.canAddShot()) {
        return openModal(LimitReachedModal, {
          type: 'shots',
          eventName: 'Shots exceed 10',
        });
      }

      if (!projectId) {
        return;
      }

      let frame;
      if (file) {
        const options = { width: 640, height: 380 };
        frame = await formatPicture(file, options);
      }

      await Shot.createShot(
        {
          projectId,
          position: position || 0,
          file: frame,
        },
        storyboard?._id,
      );
    };

    const handleDragEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setExpand(true);
    };

    const handleDragLeave = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setExpand(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
    };

    const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.persist();
      setExpand(false);

      const files = Array.from(e.dataTransfer.files);

      for (const file of files) {
        await handleCreateShot(file);
      }
    };

    const handleClick = async () => {
      handleCreateShot();
    };

    const currTags = [...shot.tagsIds];

    const renderProperty = (property: Property, index: number) => {
      if (!shot) return;

      const value = shot.getProperty(property._id);

      switch (property.type) {
        case PropertyType.SingleSelect:
          if (property.resourceType === 'Location') {
            return (
              <LocationSelect
                key={property._id}
                projectId={projectId}
                onChange={(locationId) => handleEdit({ locationId })}
                selectedLocationId={shot.locationId}
              />
            );
          }

          return (
            <SelectDrawer
              key={property._id}
              value={value}
              onSelect={(value) => handleEditProperty(property._id, value)}
              options={property.options.map((option) => ({ label: option, value: option }))}
              label={property.name}
            />
          );
        case PropertyType.MultiSelect:
          if (property.resourceType === 'Character') {
            return (
              <CharactersSelect
                key={property._id}
                isShotsBulkUpdate={selectedShotsIds && selectedShotsIds.length > 1}
                handleShotsBulkUpdate={({ characterIdToRemove, characterIdToAdd }) => {
                  handleEdit({ characterIdToRemove, characterIdToAdd });
                }}
                shot={shot}
              />
            );
          } else if (property.resourceType === 'Tag') {
            return (
              <Bloc key={property._id}>
                <label>Tags</label>

                <TagsViewer
                  targetId={shot._id}
                  tagsIds={shot.tagsIds}
                  targetType="shot"
                  overflow="wrap"
                  alignment="start"
                  onSelect={(tagsIds) => {
                    const { added, deleted } = compareArrays(currTags, tagsIds);
                    const tagIdToRemove = deleted.length > 0 ? deleted[0] : undefined;
                    const tagIdToAdd = added.length > 0 ? added[0] : undefined;

                    handleEdit?.({ tagIdToAdd, tagIdToRemove, tagsIds });
                  }}
                />
              </Bloc>
            );
          }

          return (
            <MultiSelectItem
              key={property._id}
              value={value && Array.isArray(value) ? value : []}
              onSelect={(value) => handleEditProperty(property._id, value)}
              options={property.options.map((option) => ({ label: option, value: option }))}
              label={property.name}
            />
          );
        case PropertyType.SmallText:
          return (
            <InputDrawer
              key={property._id}
              value={value}
              label={property.name}
              onChange={(value) => handleEditProperty(property._id, value)}
            />
          );
        case PropertyType.Number:
          return (
            <NumberInputDrawer
              value={value}
              canNeg={false}
              onChange={(value) => handleEditProperty(property._id, value)}
              label={property.name}
            />
          );
        case PropertyType.LongText:
          return (
            <TextAreaDrawer
              key={property._id}
              label={property.name}
              value={shot.getProperty(property._id)}
              onChange={(value) => handleEditProperty(property._id, value)}
            />
          );
        case PropertyType.Duration:
          /**
           * Only work with estimated time for now
           */
          return (
            <TimeInputDrawer
              key={property._id}
              value={shot.estimatedTime}
              onChange={(estimatedTime) => handleEdit({ estimatedTime })}
              label={property.name}
            />
          );
        default:
          return;
      }
    };

    const handleShotClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const modifier = getModifier(e.nativeEvent);

      if (!shot) return;

      onClick && onClick(shot._id, modifier);
    };

    if (!shot) return null;

    const renderVoiceOver = () => {
      const voiceOverProperty = properties.find((p) => p.key === 'voiceOver');
      const voiceOverValue = shot.getProperty(voiceOverProperty?._id);

      return (
        voiceOverValue &&
        storyboard &&
        storyboardPreferences &&
        storyboardPreferences.zoom > 4 && (
          <SubtitleContainer>
            <Subtitle
              dangerouslySetInnerHTML={{
                __html: voiceOverValue.replaceAll('\n', '<br>'),
              }}
            />
          </SubtitleContainer>
        )
      );
    };

    return (
      <Wrapper active={active} data-zoom={storyboardPreferences?.zoom}>
        <PageContainer ref={ref}>
          <InsertOverlay
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            data-expand={expand}
          >
            <InsertTile onClick={handleClick}>+</InsertTile>
          </InsertOverlay>

          <ShotContainer onClick={handleShotClick} style={style} {...props}>
            <Cover>
              <CoverImage
                src={shot.cover?.src || ''}
                title={shot.title}
                progress={shot.coverGenerateProgress}
                onEdit={(file) => handleEdit({ file })}
              />

              <StoryboardItemHeader
                shot={shot}
                zoomLevel={storyboardPreferences?.zoom || 1}
                storyboardId={storyboardId}
                position={position}
              />

              {renderVoiceOver()}
            </Cover>

            <Block>{properties.map(renderProperty)}</Block>
          </ShotContainer>
        </PageContainer>
      </Wrapper>
    );
  },
);

const BasicInfoWrapper = styled.div`
  display: block;
`;

const PageContainer = styled.div`
  position: relative;
  display: block;
  width: 100%;
  background-size: cover;
  border-radius: 3px;
  height: 100%;
  outline: none;
  appearance: none;
  border: none;

  &:focus-visible:not(.active &) {
    box-shadow: 0 0 0 2px #4c9ffe;
  }
`;

const ShotContainer = styled.div`
  position: relative;
  transition: 0.3s all ease;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  background: var(--card-bg-color);
  border-radius: var(--card-border-radius);
  border: var(--storyboard-border-color);
  user-select: none;
  height: 100%;
`;

const InsertTile = styled.div<any>`
  border-radius: 0.8rem;
  font-size: 1.6rem;
  font-weight: 700;
  justify-content: center;
  align-items: center;
  color: transparent;
  cursor: pointer;
  user-select: none;
`;

const BaseHoverInsertTile = css`
  width: 30%;
  right: -10%;

  & > ${InsertTile} {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    width: 40%;
    border: dashed 2px white;
    color: white;
  }

  & + div {
    transform: scale(0.8) translateX(-5%) !important;
  }
`;

const InsertOverlay = styled.div<any>`
  display: none;
  position: absolute;
  right: -1.6rem;
  top: 50%;
  width: 1.6rem;
  transition: all 0.1s ease-in-out;
  height: 80%;
  transform: translateY(-50%);
  justify-content: center;
  align-items: center;
  color: transparent;
  cursor: default;

  &:hover {
    ${BaseHoverInsertTile}
  }

  @media screen and (min-width: 768px) {
    display: flex;
  }

  &[data-expand='true'] {
    ${BaseHoverInsertTile}
    & > * {
      pointer-events: none;
    }
  }
`;

const SubtitleContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.6rem;
`;

const Subtitle = styled.div`
  z-index: 10;
  padding: 0.8rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 1.6rem;
  text-align: center;
  line-height: 2rem;
`;

const Cover = styled.div`
  position: relative;

  &:hover + #action,
  &:hover > #action,
  &:hover #action {
    visibility: initial;
  }
`;

const Footer = styled.footer`
  position: relative;
  // padding: 1.2rem;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const OrderIndicator = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 8px;
  left: 8px;
  height: 2.4rem;
  width: 2.4rem;
  border-radius: 50%;
  background-color: var(--white-default);
  color: #111414;
  font-family: Inter, sans-serif;
  font-size: 1.2rem;
  font-weight: bold;
  text-align: center;
  z-index: 500;
  cursor: grab;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div<any>`
  height: 100%;
  position: relative;
  list-style: none;
  width: 100%;
  min-width: 100%;
  opacity: ${({ active }) => (active ? 0.25 : 1)};
  transition: transform 200ms ease 0s;
  scroll-snap-align: center;

  &:hover {
    cursor: grab;
  }

  @media screen and (max-width: 640px) {
    &:nth-child(2n) {
      ${InsertTile} {
        opacity: 0;
      }
    }
  }

  @media screen and (min-width: 641px) and (max-width: 1240px) {
    &:nth-child(3n) {
      ${InsertTile} {
        opacity: 0;
      }
    }

    @media screen and (min-width: 1241px) {
      &:nth-child(4n) {
        ${InsertTile} {
          opacity: 0;
        }
      }
    }
  }

  &[data-zoom='1'],
  &[data-zoom='2'] {
    ${Footer}, ${BasicInfoWrapper}, ${Block} {
      display: none;
    }

    ${ShotContainer} {
      height: unset;
    }
  }

  &[data-zoom='3'],
  &[data-zoom='4'] {
    ${Footer} {
      display: flex;
    }

    ${BasicInfoWrapper} {
      display: block;
    }

    ${Block} {
      display: none;
    }
  }

  &[data-zoom='5'],
  &[data-zoom='6'] {
    ${BasicInfoWrapper} {
      display: block;
    }

    ${Footer}, ${Block} {
      display: flex;
    }
  }
`;
